/**
 * ARTISTTILGANGEN (PERSONTYPER-BESTILLING §2.1, §2.2, §2.4, §2.5; migrering 1080).
 *
 * Byrået gir tilgang til en PERSON eller en ORGANISASJON med tre valg. Det erstatter nivåene,
 * avhukingene og «Som resten av bandet» (`ARTIST_SHARE_SCOPES`, `bandAccess`), som står igjen bare så
 * eldre klienter tåler svarene.
 *
 *  * Omfang: `whole` (hele artisten, alle bookinger, også forespørsler og bud) eller `own_gigs`
 *    (konsertene man står på: i reisefølget eller som management på advancen, fra advancen er
 *    opprettet, uansett status — pluss artistens Personer, Dokumenter og kontaktkortet).
 *  * Økonomi: honorar og kontrakt. Avtale-fanen følger bare `whole` + økonomi.
 *  * Endring: `view` eller `edit`.
 *
 * SPERREN står i basen (`app.object_grant_artist_guard`): ingen gir mer enn de har, den som er lagt
 * til av en manager eller tour manager får alltid `own_gigs`, bare byrået gir `whole`, deler med en
 * organisasjon eller oppretter artister, og mottakerorganisasjonen deler bare med egne folk. Koden
 * basen svarer med, kommer som `details[0].code` (`ARTIST_ACCESS_REFUSALS`).
 *
 * Svaret på «hva har personen» gir `app.artist_access` og, booking for booking,
 * `app.person_booking_access`. Grensesnittet leser `access` på radene under og spør aldri selv.
 */
import { z } from 'zod';

import { concertKindSchema } from './concerts.js';
import { wireInstant } from './wire-time.js';

export const ARTIST_ACCESS_SCOPES = ['whole', 'own_gigs'] as const;
export const artistAccessScopeSchema = z
  .enum(ARTIST_ACCESS_SCOPES)
  .meta({ id: 'ArtistAccessScope' });
export type ArtistAccessScope = z.infer<typeof artistAccessScopeSchema>;

export const ARTIST_ACCESS_PERMISSIONS = ['view', 'edit'] as const;
export const artistAccessPermissionSchema = z
  .enum(ARTIST_ACCESS_PERMISSIONS)
  .meta({ id: 'ArtistAccessPermission' });
export type ArtistAccessPermission = z.infer<typeof artistAccessPermissionSchema>;

/** De tre valgene. */
export const artistAccessSchema = z
  .object({
    artistScope: artistAccessScopeSchema,
    economy: z.boolean(),
    permission: artistAccessPermissionSchema,
  })
  .meta({ id: 'ArtistAccess' });
export type ArtistAccess = z.infer<typeof artistAccessSchema>;

/** Det minste: konsertene man står på, uten økonomi, kan se. Basen fyller det samme. */
export const DEFAULT_ARTIST_ACCESS: Readonly<ArtistAccess> = Object.freeze({
  artistScope: 'own_gigs',
  economy: false,
  permission: 'view',
});

/** Avtale-fanen (avtalen mellom artist og byrå): bare hele artisten med økonomi (§2.1). */
export function artistAccessSeesAgreement(access: ArtistAccess | null | undefined): boolean {
  return access != null && access.artistScope === 'whole' && access.economy;
}

/** Om `a` gir mer enn `b` på noen av de tre: sperren i basen, speilet for skjemaet. */
export function artistAccessExceeds(a: ArtistAccess, b: ArtistAccess): boolean {
  return (
    (a.artistScope === 'whole' && b.artistScope !== 'whole') ||
    (a.economy && !b.economy) ||
    (a.permission === 'edit' && b.permission !== 'edit')
  );
}

/** Kodene sperren i basen svarer med (`details[0].code`, 400). */
export const ARTIST_ACCESS_REFUSALS = [
  'artist_share_agency_only',
  'artist_share_organization_agency_only',
  'artist_share_needs_partner',
  'artist_share_whole_agency_only',
  'artist_share_exceeds_giver',
  'artist_share_needs_edit',
  'artist_share_not_yours',
  'artist_share_outside_organization',
  'artist_share_parent_gone',
  /* API-et: innlogging krever e-post (§2.2). */
  'artist_login_needs_email',
] as const;
export type ArtistAccessRefusal = (typeof ARTIST_ACCESS_REFUSALS)[number];

/**
 * INNLOGGING ELLER «BARE OPPFØRT» (§2.2). Innlogging er standarden i dialogen når byrået legger til
 * en person, og da er e-post påkrevd. «Bare oppført» er et aktivt valg: personen står på lista og
 * følger med til arrangøren, men ingenting sendes. Det finnes ingen lenke for en artist.
 */
export const ARTIST_ACCESS_LOGINS = ['login', 'listed'] as const;
export const artistAccessLoginSchema = z
  .enum(ARTIST_ACCESS_LOGINS)
  .meta({ id: 'ArtistAccessLogin' });
export type ArtistAccessLogin = z.infer<typeof artistAccessLoginSchema>;

/**
 * Valget når en person legges i bandet eller i Management (`access` på
 * `CreateBookingArtistMemberRequest` og `CreateBookingArtistContactRequest`). Uten feltet er
 * personen bare oppført, som før; dialogen sender `login` som standard.
 */
export const artistRosterAccessSchema = z
  .object({
    login: artistAccessLoginSchema,
    /** Tilgangen når `login` er `login`. Mangler den, gjelder `DEFAULT_ARTIST_ACCESS`. */
    access: artistAccessSchema.optional(),
  })
  .meta({ id: 'ArtistRosterAccess' });
export type ArtistRosterAccess = z.infer<typeof artistRosterAccessSchema>;

/** Hvem som får tilgang: en kjent person, en e-postadresse, eller en samarbeidende organisasjon. */
export const artistAccessRecipientSchema = z
  .union([
    z.strictObject({ personId: z.uuid() }),
    z.strictObject({
      email: z.email().max(320),
      displayName: z.string().trim().min(1).max(200).optional(),
    }),
    z.strictObject({ organizationId: z.uuid() }),
  ])
  .meta({ id: 'ArtistAccessRecipient' });
export type ArtistAccessRecipient = z.infer<typeof artistAccessRecipientSchema>;

export const createArtistAccessRequestSchema = z
  .object({
    recipient: artistAccessRecipientSchema,
    access: artistAccessSchema,
    /**
     * Kontaktpersonen hos en organisasjon (§2.4): festivalen og arrangøren ser organisasjonen med
     * den. Bare for en organisasjon; personen må være medlem der.
     */
    contactPersonId: z.uuid().nullable().optional(),
    note: z.string().trim().min(1).max(500).nullable().optional(),
    /**
     * §2.2.4 (QA-A 28.10): adressen innloggingen går til, når byråets kort for en oppført person
     * mangler e-post. Bare med `personId`; skrives på kortet før invitasjonen, aldri over en adresse
     * som står der fra før.
     */
    email: z.email().max(320).optional(),
  })
  .meta({ id: 'CreateArtistAccessRequest' });
export type CreateArtistAccessRequest = z.infer<typeof createArtistAccessRequestSchema>;

export const updateArtistAccessRequestSchema = z
  .object({
    artistScope: artistAccessScopeSchema.optional(),
    economy: z.boolean().optional(),
    permission: artistAccessPermissionSchema.optional(),
    contactPersonId: z.uuid().nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateArtistAccessRequest' });
export type UpdateArtistAccessRequest = z.infer<typeof updateArtistAccessRequestSchema>;

/**
 * Tilstanden til en tilgang: `listed` = bare oppført (ingen deling), `invited` = invitasjonen til en
 * uten konto er ikke løst inn, `awaiting_answer` = Godta/Avslå i Innboks venter, `active` = gjelder.
 */
export const ARTIST_ACCESS_STATUSES = ['listed', 'invited', 'awaiting_answer', 'active'] as const;
export const artistAccessStatusSchema = z
  .enum(ARTIST_ACCESS_STATUSES)
  .meta({ id: 'ArtistAccessStatus' });
export type ArtistAccessStatus = z.infer<typeof artistAccessStatusSchema>;

const person = z.object({
  personId: z.uuid(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});

/**
 * «LAGT TIL AV [NAVN]» (§2.6): satt når en annen enn byrået ga tilgangen — en manager, en tour
 * manager, eller mottakerorganisasjonen som fordelte taket. Null når byrået la til.
 */
export const artistAccessAddedBySchema = z
  .object({
    personId: z.uuid(),
    name: z.string(),
    organizationName: z.string().nullable(),
  })
  .meta({ id: 'ArtistAccessAddedBy' });
export type ArtistAccessAddedBy = z.infer<typeof artistAccessAddedBySchema>;

/** Én tilgang i byråets liste (og i mottakerens, for det hen selv har gitt). */
export const artistAccessEntrySchema = z
  .object({
    grantId: z.uuid(),
    subject: z.discriminatedUnion('kind', [
      person.extend({ kind: z.literal('person') }),
      z.object({
        kind: z.literal('organization'),
        organizationId: z.uuid(),
        name: z.string(),
        /** «N personer har tilgang» (§2.4): taket selv (admin) og det mottakeren har fordelt. */
        personCount: z.number().int().nonnegative(),
        contact: person.nullable(),
      }),
    ]),
    access: artistAccessSchema,
    status: artistAccessStatusSchema,
    addedBy: artistAccessAddedBySchema.nullable(),
    createdAt: wireInstant,
  })
  .meta({ id: 'ArtistAccessEntry' });
export type ArtistAccessEntry = z.infer<typeof artistAccessEntrySchema>;

export const artistAccessListSchema = z
  .object({ items: z.array(artistAccessEntrySchema) })
  .meta({ id: 'ArtistAccessList' });
export type ArtistAccessList = z.infer<typeof artistAccessListSchema>;

export const artistAccessCreatedSchema = z
  .object({
    entry: artistAccessEntrySchema,
    /** `granted` = gjelder; `invited` = e-post til en uten konto; `awaiting_answer` = Innboks. */
    outcome: z.enum(['granted', 'invited', 'awaiting_answer']),
    /** Invitasjonens token, vist én gang. Bare for `invited`. */
    invitationToken: z.string().optional(),
  })
  .meta({ id: 'ArtistAccessCreated' });
export type ArtistAccessCreated = z.infer<typeof artistAccessCreatedSchema>;

/**
 * PERSONER-FANEN (§2.6): én liste med bandet, management (personer og organisasjoner) og alle som
 * har vært med på en konsert. Samme rad hos byrået og hos artisten; tilgangen er en kolonne.
 */
/** `gigs`: med på en av byråets konserter uten å stå i bandet eller i management (1130). */
export const ARTIST_PERSON_GROUPS = ['band', 'management', 'gigs'] as const;
export type ArtistPersonGroup = (typeof ARTIST_PERSON_GROUPS)[number];
export const artistPersonRowSchema = z
  .object({
    /** Stabil nøkkel for lista: `member:<id>`, `contact:<id>` eller `organization:<grantId>`. */
    key: z.string(),
    kind: z.enum(['person', 'organization']),
    personId: z.uuid().nullable(),
    organizationId: z.uuid().nullable(),
    name: z.string(),
    role: z.string().nullable(),
    instrument: z.string().nullable(),
    groups: z.array(z.enum(ARTIST_PERSON_GROUPS)),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /** Organisasjonen: hvor mange som har tilgang, og kontaktpersonen. Null for en person. */
    personCount: z.number().int().nonnegative().nullable(),
    contact: person.nullable(),
    /** Raden i bandet og i Management, for å rette eller fjerne den. Null der den ikke står. */
    memberId: z.uuid().nullable(),
    artistContactId: z.uuid().nullable(),
    grantId: z.uuid().nullable(),
    access: artistAccessSchema.nullable(),
    status: artistAccessStatusSchema,
    addedBy: artistAccessAddedBySchema.nullable(),
  })
  .meta({ id: 'ArtistPersonRow' });
export type ArtistPersonRow = z.infer<typeof artistPersonRowSchema>;

export const artistPeopleSchema = z
  .object({
    items: z.array(artistPersonRowSchema),
    /** Hva den som spør selv har på artisten. Byrået: hele, med økonomi, kan endre. */
    viewer: artistAccessSchema,
  })
  .meta({ id: 'ArtistPeople' });
export type ArtistPeople = z.infer<typeof artistPeopleSchema>;

/**
 * DELTE ARTISTER, FRA MOTTAKERENS SIDE (§2.6): hver artist merkes «Delt med deg» eller «Delt med
 * [organisasjon]». `access` er det høyeste personen har (`app.artist_access`).
 */
export const myArtistSchema = z
  .object({
    artistId: z.uuid(),
    artistName: z.string(),
    agencyId: z.uuid(),
    agencyName: z.string(),
    access: artistAccessSchema,
    /** Personens egen deling når hen har en; den gamle detaljsiden leses fra den. */
    grantId: z.uuid().nullable(),
    sharedWith: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('person') }),
      z.object({
        kind: z.literal('organization'),
        organizationId: z.uuid(),
        organizationName: z.string(),
      }),
    ]),
  })
  .meta({ id: 'MyArtist' });
export type MyArtist = z.infer<typeof myArtistSchema>;

export const myArtistListSchema = z
  .object({ items: z.array(myArtistSchema) })
  .meta({ id: 'MyArtistList' });
export type MyArtistList = z.infer<typeof myArtistListSchema>;

/**
 * MASSEOPERASJONEN (§2.5): en person eller en organisasjon på flere bookinger samtidig, fra
 * Bookinger/Konserter og fra Artister. En person havner i reisefølget (og i bandet hvis hen ikke står
 * der); en organisasjon blir management på bookingen. Bookinger personen ikke kan legges på, står i
 * `skipped` med grunnen; resten skrives.
 */
export const MAX_BOOKING_ASSIGNMENTS = 100;
export const bookingRefSchema = z
  .object({ kind: concertKindSchema, id: z.uuid() })
  .meta({ id: 'BookingRef' });
export type BookingRef = z.infer<typeof bookingRefSchema>;

export const assignToBookingsRequestSchema = z
  .object({
    recipient: z.union([
      z.strictObject({ personId: z.uuid() }),
      z.strictObject({ organizationId: z.uuid() }),
    ]),
    bookings: z.array(bookingRefSchema).min(1).max(MAX_BOOKING_ASSIGNMENTS),
    /**
     * Kontaktpersonen festivalen ser for organisasjonen på advancen (§2.4). Bare med `organizationId`,
     * og personen må være medlem der. Uten den står organisasjonen selv.
     */
    contactPersonId: z.uuid().optional(),
  })
  .meta({ id: 'AssignToBookingsRequest' });
export type AssignToBookingsRequest = z.infer<typeof assignToBookingsRequestSchema>;

export const ASSIGNMENT_SKIP_REASONS = [
  'not_found',
  'already_there',
  'not_on_artist',
  'no_access',
  'not_partner',
] as const;
export const assignToBookingsResultSchema = z
  .object({
    added: z.array(bookingRefSchema),
    skipped: z.array(bookingRefSchema.extend({ reason: z.enum(ASSIGNMENT_SKIP_REASONS) })),
  })
  .meta({ id: 'AssignToBookingsResult' });
export type AssignToBookingsResult = z.infer<typeof assignToBookingsResultSchema>;

/**
 * SLETT EGEN KONTO (§2.6, personvern). Bekreftelsen er e-postadressen skrevet inn på nytt, så et
 * feiltrykk aldri sletter noe.
 */
export const deleteMyAccountRequestSchema = z
  .object({ confirmEmail: z.email().max(320) })
  .meta({ id: 'DeleteMyAccountRequest' });
export type DeleteMyAccountRequest = z.infer<typeof deleteMyAccountRequestSchema>;

/**
 * 284.5 (1151): «Slett kontoen min» venter i 30 dager. Svaret på forespørselen, og det `/v1/me` viser
 * mens fristen løper: når personen ba om det, og når kontoen anonymiseres. Kontoen er sperret
 * (`ACCOUNT_CLOSING`) til da, og «Angre» (`POST /v1/me/account/restore`) gir alt tilbake.
 */
export const accountDeletionSchema = z
  .object({ requestedAt: z.iso.datetime(), dueAt: z.iso.datetime() })
  .meta({ id: 'AccountDeletion' });
export type AccountDeletion = z.infer<typeof accountDeletionSchema>;

/**
 * «KAN ENDRE» FRA MINE KONSERTER (§2.1, 1089): legg en person i reisefølget på bookingen, og gi hen
 * innlogging med det giveren selv har (aldri mer; alltid «Konsertene man står på»). Personen er en
 * med Tvitvi (id), eller en e-post med en person bak.
 */
export const addConcertPartyRequestSchema = z
  .object({
    recipient: z.union([
      z.strictObject({ personId: z.uuid() }),
      z.strictObject({ email: z.email().max(320) }),
    ]),
    role: z.string().trim().min(1).max(200).nullable().optional(),
    /** Innlogging: «Kan se»/«Kan endre» og økonomi, innenfor giverens. Uten feltet: bare oppført. */
    login: z
      .object({ economy: z.boolean(), permission: artistAccessPermissionSchema })
      .nullable()
      .optional(),
  })
  .meta({ id: 'AddConcertPartyRequest' });
export type AddConcertPartyRequest = z.infer<typeof addConcertPartyRequestSchema>;

export const concertPartyChangeSchema = z
  .object({
    outcome: z.enum(['added', 'already_there', 'removed']),
    personId: z.uuid(),
    /** Innloggingen som ble gitt, når den ble bedt om. */
    grantId: z.uuid().nullable(),
  })
  .meta({ id: 'ConcertPartyChange' });
export type ConcertPartyChange = z.infer<typeof concertPartyChangeSchema>;

/**
 * HELE ARTISTEN + «KAN ENDRE» FRA ARTISTSIDEN (1120, sak 9). Byråets artisttabeller nås aldri av en
 * gjest, så artistsiden skriver gjennom egne ruter (`/v1/me/artists/:artistId/...`) med tilgangen som
 * vakt. Honorar, pris og vilkår sendes bare med økonomi; ellers svarer API-et
 * `artist_share_needs_economy`. Lesingen er den delte artisten (`SharedArtistView`).
 */
const memberText = z.string().trim().max(200).nullable().optional();
export const artistSideMemberRequestSchema = z
  .object({
    /** En kjent person, eller et nytt navn (med e-post om man har den). */
    personId: z.uuid().nullable().optional(),
    displayName: z.string().trim().min(1).max(200).nullable().optional(),
    email: z.email().max(320).nullable().optional(),
    role: memberText,
    instrument: memberText,
  })
  .refine((v) => v.personId != null || v.displayName != null || v.email != null, {
    message: 'Give a person, a name or an email.',
    path: ['personId'],
  })
  .meta({ id: 'ArtistSideMemberRequest' });
export type ArtistSideMemberRequest = z.infer<typeof artistSideMemberRequestSchema>;

export const artistSideMemberUpdateSchema = z
  .object({ role: memberText, instrument: memberText, travels: z.boolean().optional() })
  .meta({ id: 'ArtistSideMemberUpdate' });
export type ArtistSideMemberUpdate = z.infer<typeof artistSideMemberUpdateSchema>;

export const artistSideFormatRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    travellingParty: z.number().int().min(0).max(200).nullable().optional(),
    setLengthMinutes: z.number().int().positive().max(1440).nullable().optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    /** Bare med økonomi. Utelatt = rør ikke prisen. */
    askingFeeMinor: z.number().int().min(0).max(100_000_000_00).nullable().optional(),
  })
  .meta({ id: 'ArtistSideFormatRequest' });
export type ArtistSideFormatRequest = z.infer<typeof artistSideFormatRequestSchema>;

export const artistSideDocumentRequestSchema = z
  .object({
    kind: z.string().trim().min(1).max(60),
    label: z.string().trim().max(200).nullable().optional(),
    url: z.url().max(2000).nullable().optional(),
    /** En fil lastet opp med `POST /v1/me/artists/:artistId/files`. */
    attachmentId: z.uuid().nullable().optional(),
  })
  .refine((v) => v.url != null || v.attachmentId != null, {
    message: 'Give a link, a file or both.',
    path: ['url'],
  })
  .meta({ id: 'ArtistSideDocumentRequest' });
export type ArtistSideDocumentRequest = z.infer<typeof artistSideDocumentRequestSchema>;

export const ARTIST_SIDE_BOOKING_STATUSES = ['tentative', 'hold', 'confirmed'] as const;
export const artistSideBookingRequestSchema = z
  .object({
    startsOn: z.iso.date(),
    endsOn: z.iso.date().nullable().optional(),
    organiserName: z.string().trim().min(1).max(200),
    venue: z.string().trim().max(200).nullable().optional(),
    status: z.enum(ARTIST_SIDE_BOOKING_STATUSES),
    formatId: z.uuid().nullable().optional(),
    /** Bare med økonomi. Utelatt = rør ikke honoraret og vilkårene. */
    feeMinor: z.number().int().min(0).nullable().optional(),
    terms: z.string().trim().max(4000).nullable().optional(),
  })
  .meta({ id: 'ArtistSideBookingRequest' });
export type ArtistSideBookingRequest = z.infer<typeof artistSideBookingRequestSchema>;

export const artistSideWriteResultSchema = z
  .object({ id: z.uuid() })
  .meta({ id: 'ArtistSideWriteResult' });
export type ArtistSideWriteResult = z.infer<typeof artistSideWriteResultSchema>;
